import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type {
  LLMIntegration,
  LLMProvider,
} from "@chat-yai/shared/admin-types";
import { integrationsApi } from "@/api/endpoints/integrations";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Modal } from "@/components/Modal";
import { FormError } from "@/components/FormError";
import { Icon } from "@/components/Icon";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { canEditIntegrations, permissionTooltip } from "@/lib/rbac";

const PROVIDERS: { value: LLMProvider; label: string; placeholder: string }[] = [
  { value: "anthropic", label: "Anthropic Claude", placeholder: "sk-ant-..." },
  { value: "openai", label: "OpenAI", placeholder: "sk-..." },
  { value: "groq", label: "Groq", placeholder: "gsk_..." },
  { value: "deepseek", label: "DeepSeek", placeholder: "sk-..." },
];

const upsertSchema = z.object({
  provider: z.enum(["anthropic", "openai", "groq", "deepseek"]),
  api_key: z.string().min(8, "Key looks too short.").max(512, "Key too long."),
  model_override: z.string().trim().max(120, "Model name too long.").optional(),
});
type UpsertValues = z.infer<typeof upsertSchema>;

export function IntegrationsLLMPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<LLMIntegration | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<LLMProvider | null>(null);

  const currentQuery = useQuery({
    queryKey: ["tenants", "current"],
    queryFn: () => tenantsApi.current(),
  });
  const role = currentQuery.data?.role ?? "viewer";
  const canEdit = canEditIntegrations(role);

  const listQuery = useQuery({
    queryKey: ["integrations", "llm"],
    queryFn: () => integrationsApi.listLlm(),
  });

  const form = useForm<UpsertValues>({
    defaultValues: { provider: "anthropic", api_key: "", model_override: "" },
  });

  const upsertMutation = useMutation({
    mutationFn: (v: UpsertValues) => {
      const parsed = upsertSchema.parse(v);
      return integrationsApi.upsertLlm({
        provider: parsed.provider,
        api_key: parsed.api_key,
        ...(parsed.model_override ? { model_override: parsed.model_override } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations", "llm"] });
      toast.push("success", "API key saved.");
      setEditing(null);
      setCreating(false);
      form.reset({ provider: "anthropic", api_key: "", model_override: "" });
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (provider: LLMProvider) => integrationsApi.removeLlm(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations", "llm"] });
      setConfirmDelete(null);
      toast.push("success", "API key removed.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const data = listQuery.data;
  const items = data?.items ?? [];
  const usedProviders = new Set(items.map((i) => i.provider));
  const availableForCreate = PROVIDERS.filter((p) => !usedProviders.has(p.value));

  const openCreate = (): void => {
    const first = availableForCreate[0]?.value ?? "anthropic";
    form.reset({ provider: first, api_key: "", model_override: "" });
    setCreating(true);
    setEditing(null);
  };
  const openEdit = (item: LLMIntegration): void => {
    form.reset({
      provider: item.provider,
      api_key: "",
      model_override: item.model_override ?? "",
    });
    setEditing(item);
    setCreating(false);
  };

  return (
    <div className="page" data-testid="integrations-llm-page">
      <div className="page-head">
        <div>
          <div className="page-title">LLM integrations</div>
          <div className="page-sub">
            Bring your own API key. Keys are encrypted before they hit the
            database; the admin UI only ever sees a masked preview.
          </div>
        </div>
        {canEdit ? (
          <div className="page-actions">
            <button
              className="btn btn-accent btn-sm"
              onClick={openCreate}
              disabled={availableForCreate.length === 0}
              title={availableForCreate.length === 0 ? "All providers configured" : ""}
              data-testid="open-add-llm"
            >
              <Icon name="plus" size={12} /> Add provider
            </button>
          </div>
        ) : null}
      </div>

      {data ? (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div className="help" style={{ margin: 0 }}>
            Platform default in use when no tenant key is configured:{" "}
            <span className="mono">
              {data.platform_default_provider} / {data.platform_default_model}
            </span>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "1.2fr 1.4fr 1.4fr 1fr 200px" }}
        >
          <div>Provider</div>
          <div>Key</div>
          <div>Model override</div>
          <div>Status</div>
          <div></div>
        </div>
        {items.map((i) => (
          <div
            key={i.provider}
            className="list-row"
            style={{ gridTemplateColumns: "1.2fr 1.4fr 1.4fr 1fr 200px" }}
            data-testid={`llm-row-${i.provider}`}
          >
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {PROVIDERS.find((p) => p.value === i.provider)?.label ?? i.provider}
            </div>
            <div className="mono" style={{ fontSize: 12 }}>
              {i.masked_key}
            </div>
            <div className="mono" style={{ fontSize: 12 }}>
              {i.model_override ?? <span className="help" style={{ margin: 0 }}>default</span>}
            </div>
            <div>
              <span className={"chip " + (i.status === "active" ? "chip-success" : "chip-warning")}>
                {i.status}
              </span>
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              {canEdit ? (
                <>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => openEdit(i)}
                    data-testid={`edit-llm-${i.provider}`}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setConfirmDelete(i.provider)}
                    data-testid={`delete-llm-${i.provider}`}
                  >
                    Delete
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-outline btn-sm" disabled title={permissionTooltip("owner")}>
                    Edit
                  </button>
                  <button className="btn btn-ghost btn-sm" disabled title={permissionTooltip("owner")}>
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 ? (
          <div className="empty">
            No tenant LLM keys yet. Conversations use the platform default until you add one.
          </div>
        ) : null}
      </div>

      <Modal
        title={editing ? `Edit ${editing.provider} key` : "Add provider"}
        open={!!editing || creating}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={upsertMutation.isPending}
              onClick={form.handleSubmit((v) => upsertMutation.mutate(v))}
              data-testid="submit-llm"
            >
              {upsertMutation.isPending ? "Saving…" : "Save key"}
            </button>
          </>
        }
      >
        <div className="field-group">
          <label>Provider</label>
          <select
            className="field-input"
            disabled={!!editing}
            data-testid="llm-provider"
            {...form.register("provider")}
          >
            {(editing ? PROVIDERS : availableForCreate).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field-group">
          <label>API key</label>
          <input
            type="password"
            autoComplete="new-password"
            className="field-input mono"
            placeholder={
              PROVIDERS.find((p) => p.value === form.watch("provider"))?.placeholder ?? ""
            }
            data-testid="llm-api-key"
            {...form.register("api_key")}
          />
          <div className="help">
            Stored encrypted. Replacing a key removes the previous one.
          </div>
          <FormError message={form.formState.errors.api_key?.message} />
        </div>
        <div className="field-group">
          <label>Model override (optional)</label>
          <input
            className="field-input mono"
            placeholder="e.g. claude-sonnet-4-5"
            data-testid="llm-model-override"
            {...form.register("model_override")}
          />
          <div className="help">Leave blank to use the platform default model for this provider.</div>
        </div>
      </Modal>

      <Modal
        title="Remove API key"
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}>
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={deleteMutation.isPending}
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete)}
              data-testid="confirm-delete-llm"
            >
              {deleteMutation.isPending ? "Removing…" : "Remove"}
            </button>
          </>
        }
      >
        {confirmDelete ? (
          <p>
            This removes the encrypted key for{" "}
            <strong>{confirmDelete}</strong>. New conversations will fall back
            to the platform default until a new key is provided.
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
